import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useArtList } from '../../../backend/useArtList';
import { useUser } from '../../../backend/useUser';
import Gallery from '../../website/Gallery';
import { Project } from '../../website/ProjectGrid';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../../ui/components/LoadingSpinner';
import Fullscreen from './Fullscreen';
import { ArtType } from '../Art';

const GalleryContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    overflow: visible;
`;

interface ArtGalleryProps {
    colormode: 'light' | 'dark';
    tabs: Array<{ name: string; icon?: string; disabled?: boolean }>
}

const ITEMS_PER_PAGE = 8;

const ArtGallery: React.FC<ArtGalleryProps> = ({ colormode, tabs }) => {
    // Separate state for each tab
    const [myArtProjects, setMyArtProjects] = useState<Project[]>([]);
    const [communityProjects, setCommunityProjects] = useState<Project[]>([]);
    
    // Separate pagination state for each tab
    const [myArtPage, setMyArtPage] = useState(1);
    const [communityPage, setCommunityPage] = useState(1);
    
    // Separate loading and hasMore states for each tab
    const [myArtLoading, setMyArtLoading] = useState(true);
    const [communityLoading, setCommunityLoading] = useState(true);
    const [myArtHasMore, setMyArtHasMore] = useState(true);
    const [communityHasMore, setCommunityHasMore] = useState(true);
    
    const [activeTab, setActiveTab] = useState('My Art');
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const { user, loading: userLoading } = useUser();
    const { listAllArt, listMyArt } = useArtList();
    const navigate = useNavigate();

    // Set initial tab based on user authentication
    useEffect(() => {
        if (!userLoading) {
            if (!user) {
                setActiveTab('Community');
            }
        }
    }, [user, userLoading]);

    // Function to convert art data to project format
    const artToProject = useCallback((art: any, isMyArt: boolean): Project => {
        return {
            id: art.id,
            name: art.art_name,
            username: art.user_tag, // Username from user_tag
            imageUrl: art.aiImage? art.aiImage : art.userPng, // Use the aiImage from the art object
            artType: art.artType,
            linkType: isMyArt ? (art.artType === '3D' ? '3d' : 'art') : null,
        };
    }, []);

    // Function to load My Art projects
    const loadMyArtProjects = useCallback(async (pageNum: number) => {
        if (userLoading || !user) return;
        
        setMyArtLoading(true);
        // console.log('Loading My Art projects with artType:', artType);
        
        try {
            const result = await listMyArt(user.id, pageNum, ITEMS_PER_PAGE);
            
            const { arts, hasMore: more } = result;
            const newProjects = arts.map(art => artToProject(art, true));
            
            setMyArtProjects(prev => pageNum === 1 ? newProjects : [...prev, ...newProjects]);
            setMyArtHasMore(more);
            
            if (pageNum === 1 && newProjects.length === 0 && !initialLoadDone && activeTab === 'My Art') {
                setActiveTab('Community');
                setInitialLoadDone(true);
            }
        } catch (error) {
            console.error('Error loading my art projects:', error);
            setMyArtHasMore(false);
            setMyArtProjects(pageNum === 1 ? [] : prev => prev);
        } finally {
            setMyArtLoading(false);
        }
    }, [artToProject, user, userLoading, listMyArt, initialLoadDone, activeTab]);

    // Function to load Community projects
    const loadCommunityProjects = useCallback(async (pageNum: number) => {
        if (userLoading) return;
        
        setCommunityLoading(true);
        // console.log('Loading Community projects with artType:', artType);
        
        try {
            const result = await listAllArt(pageNum, ITEMS_PER_PAGE);
            
            const { arts, hasMore: more } = result;
            const newProjects = arts.map(art => artToProject(art, false));
            
            setCommunityProjects(prev => pageNum === 1 ? newProjects : [...prev, ...newProjects]);
            setCommunityHasMore(more);
        } catch (error) {
            console.error('Error loading community art projects:', error);
            setCommunityHasMore(false);
            setCommunityProjects(pageNum === 1 ? [] : prev => prev);
        } finally {
            setCommunityLoading(false);
        }
    }, [artToProject, userLoading, listAllArt]);

    // Load initial projects when tab changes or user loads
    useEffect(() => {
        if (userLoading) return;
        
        if (activeTab === 'My Art') {
            if (user && myArtProjects.length === 0) {
                loadMyArtProjects(1);
            }
        } else if (activeTab === 'Community') {
            if (communityProjects.length === 0) {
                loadCommunityProjects(1);
            }
        }
        
        if (!initialLoadDone) {
            setInitialLoadDone(true);
        }
    }, [activeTab, userLoading, user, loadMyArtProjects, loadCommunityProjects, myArtProjects.length, communityProjects.length, initialLoadDone]);

    // useEffect(() => {
    //     setInitialLoadDone(false);
    //     setMyArtProjects([]);
    //     setCommunityProjects([]);
    //     setMyArtPage(1);
    //     setCommunityPage(1);
    //     setMyArtLoading(true);
    //     setCommunityLoading(true);
    //     setMyArtHasMore(true);
    //     setCommunityHasMore(true);
    // }, []);

    const handleLoadMore = useCallback(async () => {
        if (activeTab === 'My Art') {
            const nextPage = myArtPage + 1;
            setMyArtPage(nextPage);
            await loadMyArtProjects(nextPage);
        } else {
            const nextPage = communityPage + 1;
            setCommunityPage(nextPage);
            await loadCommunityProjects(nextPage);
        }
    }, [activeTab, myArtPage, communityPage, loadMyArtProjects, loadCommunityProjects]);

    const handleProjectClick = useCallback((project: Project) => {
        const rootUrl = project.artType === '3D' ? '/3d' : '/art';
        if (activeTab === 'My Art') {
            // console.log('Clicked my art:', project);
            navigate(`${rootUrl}/${project.id}`);
        } else {
            // console.log('Community project clicked:', project);
            // For community projects, show fullscreen view
            setSelectedProject(project);
        }
    }, [activeTab, navigate]);

    const handleCloseFullscreen = useCallback(() => {
        setSelectedProject(null);
    }, []);

    const handleTabChange = useCallback((tab: string) => {
        setActiveTab(tab);
    }, []);

    // Filter tabs based on user login state
    const visibleTabs = useMemo(() => {
        if (!user) {
            return []; // tabs.filter(tab => tab.name === 'Community');
        }
        return tabs;
    }, [user, tabs]);

    // Determine which projects, loading state, and hasMore to use based on active tab
    const currentProjects = activeTab === 'My Art' ? myArtProjects : communityProjects;
    const currentLoading = activeTab === 'My Art' ? myArtLoading : communityLoading;
    const currentHasMore = activeTab === 'My Art' ? myArtHasMore : communityHasMore;

    if (userLoading) {
        return <LoadingSpinner />;
    }

    return (
        <GalleryContainer>
            <Gallery
                type="Art"
                colormode={colormode}
                projects={currentProjects}
                loading={currentLoading}
                hasMore={currentHasMore}
                onLoadMore={handleLoadMore}
                onProjectClick={handleProjectClick}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                tabs={visibleTabs}
                userLoading={userLoading}
            />
            {selectedProject && (
                <Fullscreen
                    imageUrl={selectedProject.imageUrl}
                    projectName={selectedProject?.name}
                    username={selectedProject?.username}
                    onClose={handleCloseFullscreen}
                />
            )}
        </GalleryContainer>
    );
};

export default ArtGallery;